import { useQuery } from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import { hasPaidAccess } from "~/utils/onboarding";
import { api } from "~/api/client";
import { unwrap } from "@brad-tipper/app-frontend";

type SubscriptionTier = "pro" | "unsubscribed";

function subscriptionCapabilities(tier: SubscriptionTier) {
  return {
    tier,
    plan: tier,
    isTrial: false,
    isStarter: false,
    isStudio: tier === "pro",
    hasTrialAccess: false,
    hasCoreAccess: tier === "pro",
    hasPaidAccess: tier === "pro",
    canUseTeamAccess: tier === "pro",
    canUseRecurringInvoices: tier === "pro",
    canUseAutomatedReminders: tier === "pro",
    canUseAdvancedReports: tier === "pro",
    canUseExports: tier === "pro",
    canUseConfigurableSetAside: tier === "pro",
    hasAccess: tier === "pro",
    isExpired: tier === "unsubscribed",
  };
}

export function useSubscription() {
  const { user } = useAuth();
  const tier: SubscriptionTier = hasPaidAccess(user) ? "pro" : "unsubscribed";
  return subscriptionCapabilities(tier);
}

export function useWorkspaceSubscription(workspaceId: string | undefined) {
  const personalSubscription = useSubscription();
  const query = useQuery({
    queryKey: ["mintstint-workspace-subscription", workspaceId ?? ""],
    queryFn: async () => {
      if (!workspaceId) return null;
      const workspace = await unwrap(
        api.getWorkspace({ path: { id: workspaceId } }),
      );
      return workspace.access_tier === "pro" ? "pro" : "unsubscribed";
    },
    enabled: Boolean(workspaceId),
  });
  const tier = workspaceId
    ? ((query.data as SubscriptionTier | null) ?? "unsubscribed")
    : personalSubscription.tier;

  return {
    ...subscriptionCapabilities(tier),
    isLoading: query.isLoading,
    error: query.error,
  };
}
